import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { MFAChallenge } from "@/components/auth/MFAChallenge";
import { MFAEnroll } from "@/components/auth/MFAEnroll";

const Auth = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<"login" | "mfa" | "enroll">("login");
  const { signIn, resetPassword, user, mfaEnabled, aal, isAdmin, signOut, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [resetEmail, setResetEmail] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState<number>(0);
  const [lockoutTime, setLockoutTime] = useState<number | null>(null);
  const [countdown, setCountdown] = useState<number>(0);

  // Load lockout state on mount
  useEffect(() => {
    const attempts = localStorage.getItem("login_failed_attempts");
    const until = localStorage.getItem("login_lockout_until");
    if (attempts) setFailedAttempts(parseInt(attempts, 10));
    if (until) {
      const untilMs = parseInt(until, 10);
      if (untilMs > Date.now()) {
        setLockoutTime(untilMs);
        setCountdown(Math.ceil((untilMs - Date.now()) / 1000));
      }
    }
  }, []);

  // Handle countdown timer
  useEffect(() => {
    if (!lockoutTime) return;

    const interval = setInterval(() => {
      const remaining = Math.ceil((lockoutTime - Date.now()) / 1000);
      if (remaining <= 0) {
        setLockoutTime(null);
        setCountdown(0);
        localStorage.removeItem("login_lockout_until");
        clearInterval(interval);
      } else {
        setCountdown(remaining);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lockoutTime]);

  // Check if user signed in via OAuth (Azure) — they don't need TOTP
  const isOAuthUser = user?.app_metadata?.provider === "azure";

  useEffect(() => {
    if (user && !authLoading) {
      if (isAdmin && isOAuthUser) {
        // OAuth user — Microsoft already handled MFA, go straight to admin
        navigate("/admin");
      } else if (isAdmin && mfaEnabled && aal === "aal2") {
        // Fully authenticated with MFA — go to admin
        navigate("/admin");
      } else if (isAdmin && !isOAuthUser && mfaEnabled && aal !== "aal2") {
        // MFA enrolled but not verified yet — show TOTP challenge
        setStep("mfa");
      } else if (isAdmin && !isOAuthUser && !mfaEnabled) {
        // Admin without MFA — force enrollment
        setStep("enroll");
      }
    }
  }, [user, isAdmin, mfaEnabled, aal, navigate, authLoading, isOAuthUser]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (lockoutTime && lockoutTime > Date.now()) {
      toast.error(`Account temporarily locked. Try again in ${countdown}s.`);
      return;
    }

    setIsLoading(true);

    const { error } = await signIn(loginData.email, loginData.password);

    if (error) {
      const nextAttempts = failedAttempts + 1;
      setFailedAttempts(nextAttempts);
      localStorage.setItem("login_failed_attempts", nextAttempts.toString());

      if (nextAttempts >= 5) {
        let lockoutDuration = 30; // default 30s
        if (nextAttempts === 6) lockoutDuration = 60; // 1 min
        if (nextAttempts >= 7) lockoutDuration = 300; // 5 mins

        const until = Date.now() + lockoutDuration * 1000;
        setLockoutTime(until);
        setCountdown(lockoutDuration);
        localStorage.setItem("login_lockout_until", until.toString());
        toast.error(`Too many failed attempts. Access locked for ${lockoutDuration} seconds.`);
      } else {
        toast.error(`${error.message} (${5 - nextAttempts} attempts remaining before lockout)`);
      }
      setIsLoading(false);
    } else {
      // Clear failed attempts on successful login
      setFailedAttempts(0);
      setLockoutTime(null);
      localStorage.removeItem("login_failed_attempts");
      localStorage.removeItem("login_lockout_until");
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setIsLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "azure",
      options: {
        redirectTo: `${window.location.origin}/auth`,
        scopes: "email profile",
      },
    });
    if (error) {
      toast.error(error.message || "Failed to sign in with Microsoft");
      setIsLoading(false);
    }
    // If successful, the browser redirects — no need to setIsLoading(false)
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) return;
    
    setIsLoading(true);
    const { error } = await resetPassword(resetEmail);
    
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Password reset instructions sent to your email");
      setIsResetting(false);
    }
    setIsLoading(false);
  };

  const getDescription = () => {
    switch (step) {
      case "mfa": return "Verify your identity";
      case "enroll": return "Set up account security";
      default: return isResetting ? "Reset your password" : "Sign in to manage your dealership";
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-heading">Car Street Admin</CardTitle>
          <CardDescription>{getDescription()}</CardDescription>
        </CardHeader>
        <CardContent>
          {step === "enroll" ? (
            <MFAEnroll
              onSuccess={() => {
                // After enrollment, the onAuthStateChange will fire,
                // checkMFAStatus will update mfaEnabled=true and aal=aal2,
                // and the useEffect will navigate to /admin
              }}
              onCancel={() => {
                signOut();
                setStep("login");
              }}
            />
          ) : step === "mfa" ? (
            <MFAChallenge 
              onSuccess={() => {
                // The useEffect watching aal will handle navigation
                // once checkMFAStatus updates aal to aal2
              }}
              onCancel={() => {
                signOut();
                setStep("login");
              }}
            />
          ) : isResetting ? (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="admin@carstreet.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send Reset Link
              </Button>
              <div className="text-center mt-4">
                <Button variant="link" type="button" onClick={() => setIsResetting(false)} className="text-sm">
                  Back to Login
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="admin@carstreet.com"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="login-password">Password</Label>
                <Input
                  id="login-password"
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                />
              </div>
              <div className="flex items-center justify-between">
                <Button variant="link" type="button" onClick={() => setIsResetting(true)} className="px-0 font-normal text-xs text-muted-foreground">
                  Forgot password?
                </Button>
              </div>
              {lockoutTime && lockoutTime > Date.now() && (
                <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-md text-center font-medium my-2">
                  Too many failed attempts. Try again in {countdown} seconds.
                </div>
              )}
              <Button type="submit" className="w-full" disabled={isLoading || (!!lockoutTime && lockoutTime > Date.now())}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Sign In
              </Button>

              {/* Divider */}
              <div className="relative my-2">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">or</span>
                </div>
              </div>

              {/* Microsoft OAuth */}
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2"
                onClick={handleMicrosoftLogin}
                disabled={isLoading || (!!lockoutTime && lockoutTime > Date.now())}
              >
                <svg className="h-4 w-4" viewBox="0 0 21 21" fill="none">
                  <rect x="1" y="1" width="9" height="9" fill="#F25022" />
                  <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
                  <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
                  <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                </svg>
                Sign in with Microsoft
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
